import React, { JSX, useCallback, useState } from "react";

import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import SplitButtonOption from "./SplitButtonOption";

export type SplitButtonProps = {
	startIcon?: JSX.Element;
	options: Array<string>;

	onClick: (index: number) => void;
};

export default function SplitButton(
	props: Readonly<SplitButtonProps>
): JSX.Element {
	const anchorRef = React.useRef<HTMLDivElement>(null);

	const [open, setOpen] = useState(false);

	const handleClick = useCallback(() => {
		props.onClick(0);
	}, [props.onClick]);

	const handleMenuItemClick = useCallback(
		(option: number) => {
			props.onClick(option + 1);
			setOpen(false);
		},
		[props.onClick]
	);

	const handleToggle = useCallback(() => {
		setOpen(previous => !previous);
	}, []);

	const handleClose = useCallback(() => {
		setOpen(false);
	}, []);

	if (props.options.length === 0) {
		return <></>;
	}

	return (
		<>
			<ButtonGroup variant="contained" ref={anchorRef}>
				<Button startIcon={props.startIcon} onClick={handleClick}>
					{props.options[0]}
				</Button>
				{props.options.length > 1 && (
					<Button size="small" onClick={handleToggle}>
						<ArrowDropDownIcon />
					</Button>
				)}
			</ButtonGroup>
			<Popper
				sx={{ zIndex: 1, width: anchorRef.current?.clientWidth }}
				open={open}
				anchorEl={anchorRef.current}
				transition
				disablePortal>
				{({ TransitionProps }) => (
					<Grow
						{...TransitionProps}
						style={{
							transformOrigin: "left bottom"
						}}>
						<Paper>
							<ClickAwayListener onClickAway={handleClose}>
								<MenuList autoFocusItem>
									{props.options
										.slice(1)
										.map((option, index) => (
											<SplitButtonOption
												key={option}
												index={index}
												text={option}
												onClick={handleMenuItemClick}
											/>
										))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>
		</>
	);
}
